import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de sucursales le pide a la API, en un solo lugar.
//
// **Esta es la parte de M-1 que se copia.** Cada maestra tiene su `datos.ts` con
// la misma forma: una clave de caché con el taller adentro, una consulta por
// lectura y una función por escritura que devuelve el `ProblemDetails` o nada.
// Las pantallas no llaman a `api` directo.
//
// Los nombres de campo son los del contrato (`isActive`, `staffCount`) y no se
// traducen: vienen del cliente generado, y un error de nombre falla al compilar.

export type Sucursal = components['schemas']['LocationListItem'];
export type FichaSucursal = components['schemas']['LocationDetail'];
export type Persona = components['schemas']['StaffListItem'];

/// Lo que se manda al crear o editar. `code` vacío al crear deja que lo proponga
/// el correlativo del taller.
export interface ValoresSucursal {
  code: string;
  name: string;
  phone: string;
  address: string;
}

export interface FiltrosSucursales {
  buscar: string;
  estado: 'todas' | 'activas' | 'inactivas';
  cursor: string | null;
}

/// El taller de la pestaña (#205). **Va en toda clave de caché**: en la computadora
/// compartida de recepción, salir y entrar con alguien de otro taller sin
/// recargar no puede mostrar las sucursales del anterior. Es la regla 5 de
/// multitenancy, en el navegador.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['sucursales', taller] as const,
  lista: (taller: string, filtros: FiltrosSucursales) => ['sucursales', taller, 'lista', filtros] as const,
  ficha: (taller: string, id: string) => ['sucursales', taller, 'ficha', id] as const,
  personal: (taller: string) => ['personal', taller, 'todo'] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** Espacios afuera, y vacío pasa a `null`: la API guarda «sin teléfono», no un texto vacío. */
const opcional = (texto: string) => texto.trim() || null;

export function useSucursales(filtros: FiltrosSucursales) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/locations', {
        params: {
          // Solo lo que tiene valor: un  vacío es un filtro, no su ausencia.
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.estado !== 'todas' && { isActive: filtros.estado === 'activas' }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista de sucursales');
      return data;
    },
    // Mientras llega la página siguiente —o el resultado de lo que se está
    // escribiendo en el buscador— se sigue viendo la anterior. Sin esto la tabla
    // parpadea a «Cargando…» en cada tecla.
    placeholderData: keepPreviousData,
  });
}

export function useFichaSucursal(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/locations/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: la sucursal no existe (o es de otro
      // taller, que para esta sesión es lo mismo). La pantalla lo dice distinto.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer la sucursal');
      return data;
    },
    // La ficha siempre fresca al abrir la edición: con la versión vieja en caché,
    // guardar chocaría contra la edición de otro que ya estaba en la base.
    staleTime: 0,
  });
}

/**
 * Todo el personal activo, para la ventana de «Asignar personal». Se recorren
 * las páginas hasta el final: la ventana marca y desmarca sobre la lista entera,
 * y un taller mediano tiene decenas de personas, no miles.
 */
export function usePersonalActivo(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.personal(taller),
    enabled: habilitado,
    staleTime: 0,
    queryFn: async () => {
      const todas: Persona[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/staff', {
          params: { query: { isActive: true, limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudo leer el personal');
        todas.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todas;
    },
  });
}

/**
 * Las escrituras. Cada una devuelve el `ProblemDetails` si la API dijo que no, o
 * nada si salió bien —la forma que espera `useFormulario`—, y al salir bien
 * invalida todo lo de sucursales del taller: el conteo de personal, la marca de
 * principal y el estado cambian en más de una fila a la vez.
 */
export function useCambiosSucursal() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown }>(peticion: Promise<T>): Promise<ProblemaApi | undefined> {
    const { error } = await peticion;
    if (error) return error as ProblemaApi;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return undefined;
  }

  return {
    crear: (v: ValoresSucursal) =>
      tras(
        api.POST('/api/locations', {
          body: {
            name: v.name.trim(),
            code: opcional(v.code),
            phone: opcional(v.phone),
            address: opcional(v.address),
          },
        }),
      ),

    editar: (id: string, versionActual: number, v: ValoresSucursal) =>
      tras(
        api.PUT('/api/locations/{id}', {
          params: { path: { id } },
          body: {
            version: versionActual,
            code: v.code.trim(),
            name: v.name.trim(),
            phone: opcional(v.phone),
            address: opcional(v.address),
          },
        }),
      ),

    cambiarEstado: (id: string, activa: boolean) =>
      tras(api.PUT('/api/locations/{id}/status', { params: { path: { id } }, body: { isActive: activa } })),

    nombrarPrincipal: (id: string) => tras(api.PUT('/api/locations/{id}/main', { params: { path: { id } } })),

    eliminar: (id: string) => tras(api.DELETE('/api/locations/{id}', { params: { path: { id } } })),

    /**
     * Quién trabaja en la sucursal. La API lo guarda **desde la persona** —la
     * sucursal es parte del agregado `Staff`, no al revés—, así que marcar a
     * alguien es mandar sus sucursales de siempre más esta, y desmarcarlo, menos
     * esta. Solo viajan las personas que cambiaron.
     *
     * Se manda una por una y se corta en el primer rechazo: lo que ya se guardó
     * queda guardado, y al volver a abrir la ventana se ve el estado real.
     */
    asignarPersonal: async (sucursal: string, personal: Persona[], marcadas: ReadonlySet<string>) => {
      // Se refresca también el personal, no solo las sucursales: cada PUT le cambia
      // la versión a esa persona, y con la lista vieja en caché el siguiente
      // intento chocaría consigo mismo como si alguien más la hubiera editado.
      const refrescar = () =>
        Promise.all([
          cache.invalidateQueries({ queryKey: claves.todo(taller) }),
          cache.invalidateQueries({ queryKey: claves.personal(taller) }),
        ]);

      const cambios = personal.filter((p) => marcadas.has(String(p.id)) !== estaEn(p, sucursal));

      for (const p of cambios) {
        const actuales = p.locations.map(String).filter((l) => l !== sucursal);
        const locations = marcadas.has(String(p.id)) ? [...actuales, sucursal] : actuales;

        const { error } = await api.PUT('/api/staff/{id}/locations', {
          params: { path: { id: String(p.id) } },
          body: { version: version(p.version), locations },
        });
        if (error) {
          await refrescar();
          return error as ProblemaApi;
        }
      }

      await refrescar();
      return undefined;
    },
  };
}

/** Si la persona ya trabaja en esa sucursal. */
export const estaEn = (p: Persona, sucursal: string) => p.locations.map(String).includes(sucursal);
